import {
  Box,
  darken,
  Paper,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { FailedLoad } from '../FailedLoad/FailedLoad';
import { Loading } from '../Loading/Loading';

export interface BarChartsProps {
  loading: boolean;
  error: boolean;
  data: any;
  title: string;
  xKey: string;
  valueKey: string;
  barColor?: string;
  sx?: SxProps<Theme>;
  valueFormatter?: (value: any) => string;
}

export const BarCharts = ({
  loading,
  error,
  data,
  title,
  xKey,
  valueKey,
  barColor,
  sx,
  valueFormatter,
}: BarChartsProps) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const mergedSx = useMemo<SxProps<Theme>>(() => {
    const baseStyles: SxProps<Theme> = {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      width: '100%',
    };

    if (!sx) return baseStyles;

    if (Array.isArray(sx)) {
      return [baseStyles, ...sx] as SxProps<Theme>;
    }

    return { ...baseStyles, ...sx } as SxProps<Theme>;
  }, [sx]);

  return (
    <Box component={Paper} sx={mergedSx}>
      <Typography variant="h6" component="div" gutterBottom>
        {title}
      </Typography>
      <ResponsiveContainer width="100%" height={300}>
        {loading ? (
          <Loading />
        ) : error ? (
          <FailedLoad />
        ) : (
          <BarChart
            width={undefined as any}
            height={300}
            data={data}
            barSize={30}
          >
            <XAxis
              dataKey={xKey}
              scale="point"
              padding={{ left: 20, right: 10 }}
            />
            <YAxis />
            <Tooltip
              contentStyle={{
                backgroundColor: darken(theme.palette.background.paper, 0.1),
              }}
              formatter={(title, name) => [
                `${valueFormatter ? valueFormatter(title) : title}`,
                t(`homeCharts.tooltipLabels.${name}`),
              ]}
            />
            {/* <CartesianGrid strokeDasharray="4" stroke="#121212" /> */}
            <Bar
              dataKey={valueKey}
              fill={barColor ?? '#1cab81'}
              background={{ fill: darken(theme.palette.background.paper, 0.1) }}
            />
          </BarChart>
        )}
      </ResponsiveContainer>
    </Box>
  );
};
